// One fluency event, from whichever surface is drilling.
//
// Ported from `hooks/use-fluency-dispatcher.ts` in
// `git@git.fugl.dev:sks/yominoki.git` @ `515ce239`. The scope/args split and
// the fire-and-forget `dispatch` beside the awaitable `dispatchAsync` are
// unchanged; the call underneath is `POST /api/fluency/event` instead of a
// server action.
//
// ONE ARGUMENT WAS DROPPED. yominoki's `DispatchArgs` carried
// `extraContext?: Partial<LearningRecordContext>`, forwarded straight into the
// learning record. No surface in the app ever passed it, and over HTTP it
// stops being a typed parameter and becomes a hole: `LearningRecordContext`
// includes `userId`, `actionId`, `treatmentId` and the fluency history, so a
// browser that could send a partial one could write whatever it liked into a
// row this app treats as evidence. The Worker builds that context itself from
// the session it already verified. If a surface ever needs to contribute to
// it, the fields it may contribute get named in the endpoint's schema.

import { useCallback, useMemo } from "react";
import type { ResponseId } from "@bindery/shared/contracts/fluency";
import type {
  LearningPosition,
  TreatmentId,
} from "@bindery/shared/contracts/learning";
import type { FluencyEventResult } from "../api/contract";
import { recordFluencyEvent } from "../lib/api";

export interface FluencyDispatcherScope {
  sessionId: string;
  treatmentId: TreatmentId;
  capStrongPromotion?: boolean;
}

export interface DispatchArgs {
  surface: string;
  response: ResponseId;
  position?: LearningPosition;
}

export function useFluencyDispatcher(scope: FluencyDispatcherScope) {
  const dispatchAsync = useCallback(
    async (args: DispatchArgs): Promise<FluencyEventResult | null> => {
      const res = await recordFluencyEvent({
        surface: args.surface,
        response: args.response,
        treatmentId: scope.treatmentId,
        sessionId: scope.sessionId,
        position: args.position,
        capStrongPromotion: scope.capStrongPromotion,
      });
      if (!res.ok) {
        console.error("fluency dispatch failed:", res.reason);
        return null;
      }
      return res.result;
    },
    [scope.sessionId, scope.treatmentId, scope.capStrongPromotion],
  );

  const dispatch = useCallback(
    (args: DispatchArgs): void => {
      // Deliberately not awaited: the tile the reader tapped has already
      // moved, and a network round-trip must not gate the animation. A
      // rejection is a console error, not a UI state — the level the server
      // computed is not shown anywhere yet.
      void dispatchAsync(args).catch((error: unknown) => {
        console.error("fluency dispatch failed:", error);
      });
    },
    [dispatchAsync],
  );

  return useMemo(
    () => ({ dispatch, dispatchAsync }),
    [dispatch, dispatchAsync],
  );
}
